import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, DocumentTextIcon, LanguageIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { newsroomApi } from '@/services/newsroomApi';
import { audioToVideoApi } from '@/services/audioToVideoApi';
import { textToSpeechApi } from '@/services/audioApi';
import FactCheckPanel from '@/components/Newsroom/FactCheckPanel';
import LocalizedTextInput from '@/components/Common/LocalizedTextInput';
import LocalizedTextArea from '@/components/Common/LocalizedTextArea';
import AssignButton from '@/components/Content/AssignButton';
import ContentActivityPanel from '@/components/Content/ContentActivityPanel';
import LineagePanel from '@/components/Content/LineagePanel';
import ReviewPanel from '@/components/Content/ReviewPanel';
import NextActionsPanel from '@/components/Content/NextActionsPanel';

type ContentTab = 'original' | 'translation' | 'factcheck';

const ArticleReviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const articleId = id!;

  const [translatedTitle, setTranslatedTitle] = useState('');
  const [translatedBody, setTranslatedBody] = useState('');
  const [editorNotes, setEditorNotes] = useState('');
  const [contentTab, setContentTab] = useState<ContentTab>('translation');

  const { data: article, isLoading } = useQuery({
    queryKey: ['newsroom-article', articleId],
    queryFn: () => newsroomApi.getArticle(articleId),
  });

  // Once published, look up any video already rendered from this article's
  // TTS job so the page can link straight to it instead of just the audio —
  // sorted newest-first server-side, so [0] is the most recent attempt.
  const { data: videoJobsData } = useQuery({
    queryKey: ['audio-to-video-jobs-for-article', article?.publishedTtsJobId],
    queryFn: () => audioToVideoApi.getJobs({ ttsJobId: article!.publishedTtsJobId! }),
    enabled: article?.status === 'published' && !!article?.publishedTtsJobId,
  });
  const linkedVideoJob = videoJobsData?.jobs?.[0];

  // article.status flips to 'published' as soon as the TTS job is created —
  // generate() only validates synchronously before returning, the actual
  // synthesis runs async and can still fail afterwards. Re-check the job's
  // real status so a failed attempt shows as failed instead of "Published".
  const { data: publishedTtsJobData } = useQuery({
    queryKey: ['tts-job-for-article', article?.publishedTtsJobId],
    queryFn: () => textToSpeechApi.getTtsJob(article!.publishedTtsJobId!),
    enabled: article?.status === 'published' && !!article?.publishedTtsJobId,
  });
  const publishedTtsJob = publishedTtsJobData?.data;
  const publishFailed = article?.status === 'published' && publishedTtsJob?.status === 'failed';

  useEffect(() => {
    if (article) {
      setTranslatedTitle(article.translatedTitle || '');
      setTranslatedBody(article.translatedBody || '');
      setEditorNotes(article.factCheck?.editorNotes || '');
    }
  }, [article?._id]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['newsroom-article', articleId] });

  const saveMutation = useMutation({
    mutationFn: () => newsroomApi.updateArticle(articleId, { translatedTitle, translatedBody, editorNotes }),
    onSuccess: () => {
      toast.success('Saved');
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to save'),
  });

  const factCheckMutation = useMutation({
    mutationFn: () => newsroomApi.factCheckArticle(articleId),
    onSuccess: () => {
      toast.success('Fact-check re-run');
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Fact-check failed'),
  });

  // Retries whichever step is 'failed' (translate/fact-check). Publish
  // failures aren't retried here — re-select a voice via the Publish next action.
  const retryMutation = useMutation({
    mutationFn: () => newsroomApi.retryArticle(articleId),
    onSuccess: () => {
      toast.success('Retrying');
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Retry failed'),
  });

  if (isLoading || !article) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const canEdit = article.status === 'draft' || article.status === 'in_review';
  const translateStep = article.steps.find((s) => s.id === 'translate');
  const failedStep = article.steps.find((s) => s.status === 'failed');
  // Publish failures need a voiceId re-supplied via the "Publish" next action.
  const canRetry = !!failedStep && failedStep.id !== 'publish';

  const TABS: { id: ContentTab; label: string; icon: React.ElementType }[] = [
    { id: 'original', label: 'Original', icon: DocumentTextIcon },
    { id: 'translation', label: 'Translation', icon: LanguageIcon },
    { id: 'factcheck', label: 'Fact Check', icon: ShieldCheckIcon },
  ];

  return (
    // Sized to fill the viewport below the app's top bar (4rem) so this
    // page never scrolls as a whole — every section below manages its own
    // internal overflow instead. On narrow screens there isn't room for a
    // 3-column dashboard at all, so it falls back to a normal scrolling
    // single column there.
    <div className="lg:h-[calc(100vh-7rem)] flex flex-col overflow-y-auto lg:overflow-hidden">
      {/* Header — fixed, never scrolls */}
      <div className="flex-shrink-0">
        <button
          onClick={() => navigate('/app/newsroom')}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-2"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Newsroom
        </button>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{article.outlet}</p>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white truncate">{article.originalTitle}</h1>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
              {article.status.replace('_', ' ')}
            </span>
            <AssignButton contentType="news_article" contentId={articleId} />
            {canRetry && (
              <button
                onClick={() => retryMutation.mutate()}
                disabled={retryMutation.isLoading}
                className="px-3 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 border border-indigo-300 dark:border-indigo-700 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-900/20 disabled:opacity-50"
              >
                {retryMutation.isLoading ? 'Retrying…' : `Retry ${failedStep!.name}`}
              </button>
            )}
            {canEdit && (
              <button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isLoading}
                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
              >
                {saveMutation.isLoading ? 'Saving…' : 'Save changes'}
              </button>
            )}
          </div>
        </div>

        {publishFailed ? (
          <div className="mt-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10 px-4 py-2">
            <p className="text-sm text-red-800 dark:text-red-300">
              Publish failed — the text-to-speech job couldn't complete{publishedTtsJob?.error ? `: ${publishedTtsJob.error}` : '.'} Use the "Publish (Convert to Speech)" action below to try again.
            </p>
          </div>
        ) : article.status === 'published' && article.publishedTtsJobId && (
          <div className="mt-3 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10 px-4 py-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-green-800 dark:text-green-300">
              Published{linkedVideoJob ? ' — audio and video are ready to view.' : ' — a text-to-speech job was created from this article.'}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/app/audio/text-to-speech')}
                className="px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                View audio
              </button>
              {linkedVideoJob ? (
                <button
                  onClick={() => navigate(`/app/create-video?jobId=${linkedVideoJob.jobId}`)}
                  className="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                >
                  View video
                  {(linkedVideoJob.status === 'processing' || linkedVideoJob.status === 'queued') && ' (processing…)'}
                  {linkedVideoJob.status === 'failed' && ' (failed)'}
                </button>
              ) : (
                <button
                  onClick={() => {
                    const params = new URLSearchParams({
                      ttsJobId: article.publishedTtsJobId!,
                      title: article.translatedTitle || article.originalTitle,
                      embedTitle: '1',
                    });
                    navigate(`/app/create-video?${params.toString()}`);
                  }}
                  className="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                >
                  Create video from this
                </button>
              )}
            </div>
          </div>
        )}

        <div className="mt-3">
          <NextActionsPanel contentType="news_article" contentId={articleId} />
        </div>
      </div>

      {/* Main dashboard grid — fills remaining height, each column scrolls internally */}
      <div className="flex-1 min-h-0 mt-3 grid grid-cols-1 lg:grid-cols-12 gap-3 pb-3 lg:pb-0">
        {/* Column A — content tabs (Original / Translation / Fact Check) */}
        <div className="lg:col-span-7 min-h-0 flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="flex-shrink-0 flex border-b border-gray-200 dark:border-gray-700">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setContentTab(tab.id)}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    contentTab === tab.id
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            {contentTab === 'original' && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Original ({article.originalLanguage === 'ta' ? 'Tamil' : 'English'})
                  {article.bodyIsSummary && <span className="text-xs text-amber-600 dark:text-amber-400 ml-2">summary only</span>}
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{article.originalBody}</p>
              </div>
            )}

            {contentTab === 'translation' && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Tamil {article.translationProviderUsed && <span className="text-xs text-gray-400">via {article.translationProviderUsed}</span>}
                </h3>
                {translateStep?.status === 'processing' ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Translating…</p>
                ) : translateStep?.status === 'failed' ? (
                  <div>
                    <p className="text-sm text-red-600 dark:text-red-400">Translation failed: {translateStep.error}</p>
                    <button
                      onClick={() => retryMutation.mutate()}
                      disabled={retryMutation.isLoading}
                      className="mt-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                    >
                      {retryMutation.isLoading ? 'Retrying…' : 'Retry translation'}
                    </button>
                  </div>
                ) : (
                  <>
                    <LocalizedTextInput
                      language="ta"
                      value={translatedTitle}
                      onChange={(e) => setTranslatedTitle(e.target.value)}
                      disabled={!canEdit}
                      className="w-full mb-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm font-medium text-gray-900 dark:text-white p-2 disabled:opacity-60"
                    />
                    <LocalizedTextArea
                      language="ta"
                      value={translatedBody}
                      onChange={(e) => setTranslatedBody(e.target.value)}
                      disabled={!canEdit}
                      rows={16}
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-2 disabled:opacity-60"
                    />
                  </>
                )}
              </div>
            )}

            {contentTab === 'factcheck' && (
              <FactCheckPanel
                factCheck={article.factCheck}
                editorNotes={editorNotes}
                onEditorNotesChange={setEditorNotes}
                onRerun={() => factCheckMutation.mutate()}
                isRerunning={factCheckMutation.isLoading}
              />
            )}
          </div>
        </div>

        {/* Column B — review, lineage, and who's involved — scrolls internally */}
        <div className="lg:col-span-5 min-h-0 overflow-y-auto flex flex-col gap-3">
          <ReviewPanel contentType="news_article" contentId={articleId} />
          <LineagePanel contentType="news_article" contentId={articleId} />
          <ContentActivityPanel contentType="news_article" contentId={articleId} />
        </div>
      </div>
    </div>
  );
};

export default ArticleReviewPage;
